import { $api } from "@/kibi/core/utils/api";
import type {
  PaginatedResponse,
  QueryOptions,
} from "@/kibi/core/utils/pagination_types";
import type { InjectionKey } from "vue";
import type { Device } from "../../domain/entities/device";

export interface DeviceDataSource {
  getDevicesByBatchId(batchId: string): Promise<Device[]>;
  getDevices(options: QueryOptions): Promise<PaginatedResponse<Device[]>>;
  getAllDevices(options: QueryOptions): Promise<PaginatedResponse<Device[]>>;
  getDevice(id: string): Promise<Device>;
  claimDevices(deviceIds: string[]): Promise<void>;
  unclaimDevices(deviceIds: string[]): Promise<void>;
}

export const deviceDataSourceKey: InjectionKey<DeviceDataSource> =
  Symbol("DeviceDataSource");

export class DeviceDataSourceImpl implements DeviceDataSource {
  async getDevicesByBatchId(batchId: string): Promise<Device[]> {
    const response = await $api<{ data: Device[] }>(
      `/products/batches/${batchId}/devices`,
      {
        method: "GET",
      },
    );
    return response.data;
  }

  async getDevices(
    options: QueryOptions,
  ): Promise<PaginatedResponse<Device[]>> {
    const response = await $api<PaginatedResponse<Device[]>>(
      "/products/devices",
      {
        method: "GET",
        params: options,
      },
    );
    return response;
  }

  async getAllDevices(
    options: QueryOptions,
  ): Promise<PaginatedResponse<Device[]>> {
    const response = await $api<PaginatedResponse<Device[]>>(
      "/products/devices/all",
      {
        method: "GET",
        params: options,
      },
    );
    return response;
  }

  async getDevice(id: string): Promise<Device> {
    const response = await $api<{ data: Device }>(`/products/devices/${id}`, {
      method: "GET",
    });
    return response.data;
  }

  async claimDevices(deviceIds: string[]): Promise<void> {
    await $api("/products/devices/claim", {
      method: "POST",
      body: { device_ids: deviceIds },
    });
  }

  async unclaimDevices(deviceIds: string[]): Promise<void> {
    await $api("/products/devices/unclaim", {
      method: "POST",
      body: { device_ids: deviceIds },
    });
  }
}
